import { z } from 'zod';

import { CashDenominationRowsSchema } from './cash-denominations.schema.js';
import { CashOperationIdentityDtoSchema } from './cash-operation-identity.schema.js';
import {
  cashModeActivationStatusValues,
  cashModeScopeTypeValues,
  cashControlEntitlementObservationSourceValues,
  cashControlEntitlementTransitionEffectValues,
  cashOperatingModeValues,
  cashScopeModeTransitionValues,
  type CancelCashModeActivationRequest,
  type CashModeActivationCountDto,
  type CashModeActivationDto,
  type CashModeActivationStatus,
  type CashModeScopeType,
  type CashOperatingConfigurationDto,
  type CashOperatingMode,
  type CashScopeModeStateDto,
  type CashScopeModeTransition,
  type ConfirmCashModeActivationRequest,
  type ConfirmCashModeActivationResponse,
  type CancelCashModeActivationResponse,
  type CashControlEntitlementObservationSource,
  type CashControlEntitlementTransitionEffect,
  type GetCashOperatingConfigurationRequest,
  type GetCashOperatingConfigurationResponse,
  type GetCashScopeModeStateRequest,
  type GetCashScopeModeStateResponse,
  type ReconcileCashControlEntitlementRequest,
  type ReconcileCashControlEntitlementResponse,
  type RecordCashModeActivationCountsRequest,
  type RecordCashModeActivationCountsResponse,
  type StartCashModeActivationRequest,
  type StartCashModeActivationResponse,
  type UpdateCashOperatingConfigurationRequest,
  type UpdateCashOperatingConfigurationResponse,
} from './cash-operating-mode.dto.js';

const timestampSchema = z.iso.datetime();

export const CashOperatingModeSchema: z.ZodType<CashOperatingMode> =
  z.enum(cashOperatingModeValues);
export const CashScopeModeTransitionSchema: z.ZodType<CashScopeModeTransition> = z.enum(
  cashScopeModeTransitionValues,
);
export const CashModeScopeTypeSchema: z.ZodType<CashModeScopeType> =
  z.enum(cashModeScopeTypeValues);
export const CashModeActivationStatusSchema: z.ZodType<CashModeActivationStatus> = z.enum(
  cashModeActivationStatusValues,
);
export const CashControlEntitlementObservationSourceSchema: z.ZodType<CashControlEntitlementObservationSource> =
  z.enum(cashControlEntitlementObservationSourceValues);
export const CashControlEntitlementTransitionEffectSchema: z.ZodType<CashControlEntitlementTransitionEffect> =
  z.enum(cashControlEntitlementTransitionEffectValues);

export const CashOperatingConfigurationDtoSchema: z.ZodType<CashOperatingConfigurationDto> =
  z.object({
    companyId: z.uuid(),
    targetOperatingMode: CashOperatingModeSchema,
    usesCentralTreasury: z.boolean(),
    allowDirectVaultIntake: z.boolean(),
    cashierSelfRefillPerTransactionLimitMinor: z.number().int().nonnegative(),
    allowDrawerToDrawer: z.boolean(),
    revision: z.number().int().positive(),
    updatedAt: timestampSchema,
  });

export const CashScopeModeStateDtoSchema: z.ZodType<CashScopeModeStateDto> = z.object({
  scopeType: CashModeScopeTypeSchema,
  scopeId: z.uuid(),
  effectiveMode: CashOperatingModeSchema,
  transition: CashScopeModeTransitionSchema,
  configurationRevision: z.number().int().positive(),
  capturedEntitlementRevision: z.number().int().nonnegative(),
});

export const CashModeActivationCountDtoSchema: z.ZodType<CashModeActivationCountDto> = z
  .object({
    cashLocationId: z.uuid(),
    cashSessionId: z.uuid().nullable(),
    checkpointId: z.uuid(),
    rows: CashDenominationRowsSchema,
    totalMinor: z.number().int().nonnegative(),
  })
  .superRefine((value, context) => {
    const rowTotal = value.rows.reduce((sum, row) => sum + row.denom * row.count, 0);
    if (rowTotal !== value.totalMinor) {
      context.addIssue({
        code: 'custom',
        path: ['totalMinor'],
        message: 'Activation count rows must equal totalMinor.',
      });
    }
  });

export const CashModeActivationDtoSchema: z.ZodType<CashModeActivationDto> =
  CashOperationIdentityDtoSchema.extend({
    id: z.uuid(),
    status: CashModeActivationStatusSchema,
    scopeType: CashModeScopeTypeSchema,
    scopeId: z.uuid(),
    targetMode: CashOperatingModeSchema,
    effectiveMode: CashOperatingModeSchema,
    transition: CashScopeModeTransitionSchema,
    configurationRevision: z.number().int().positive(),
    capturedEntitlementRevision: z.number().int().nonnegative(),
    counts: z.array(CashModeActivationCountDtoSchema),
    activatedByUserId: z.uuid().nullable(),
    activatedAt: timestampSchema.nullable(),
    cancelledByUserId: z.uuid().nullable(),
    cancelledAt: timestampSchema.nullable(),
    createdAt: timestampSchema,
    updatedAt: timestampSchema,
  });

export const UpdateCashOperatingConfigurationRequestSchema: z.ZodType<UpdateCashOperatingConfigurationRequest> =
  z.object({
    companyId: z.uuid(),
    operationId: z.uuid(),
    expectedRevision: z.number().int().positive(),
    targetOperatingMode: CashOperatingModeSchema,
    usesCentralTreasury: z.boolean(),
    allowDirectVaultIntake: z.boolean(),
    cashierSelfRefillPerTransactionLimitMinor: z.number().int().nonnegative(),
    allowDrawerToDrawer: z.boolean(),
    occurredAt: timestampSchema.optional(),
  });

export const GetCashOperatingConfigurationRequestSchema: z.ZodType<GetCashOperatingConfigurationRequest> =
  z.object({ companyId: z.uuid() });

export const GetCashOperatingConfigurationResponseSchema: z.ZodType<GetCashOperatingConfigurationResponse> =
  z.object({ configuration: CashOperatingConfigurationDtoSchema });

export const UpdateCashOperatingConfigurationResponseSchema: z.ZodType<UpdateCashOperatingConfigurationResponse> =
  z.object({ configuration: CashOperatingConfigurationDtoSchema });

export const GetCashScopeModeStateRequestSchema: z.ZodType<GetCashScopeModeStateRequest> = z.object(
  {
    companyId: z.uuid(),
    scopeType: CashModeScopeTypeSchema,
    scopeId: z.uuid(),
  },
);

export const GetCashScopeModeStateResponseSchema: z.ZodType<GetCashScopeModeStateResponse> =
  z.object({
    state: CashScopeModeStateDtoSchema,
    activation: CashModeActivationDtoSchema.nullable(),
  });

export const StartCashModeActivationRequestSchema: z.ZodType<StartCashModeActivationRequest> = z
  .object({
    companyId: z.uuid(),
    operationId: z.uuid(),
    scopeType: CashModeScopeTypeSchema,
    scopeId: z.uuid(),
    configurationRevision: z.number().int().positive(),
    occurredAt: timestampSchema.optional(),
  })
  .strict();

export const StartCashModeActivationResponseSchema: z.ZodType<StartCashModeActivationResponse> =
  z.object({
    activation: CashModeActivationDtoSchema,
    state: CashScopeModeStateDtoSchema,
  });

export const RecordCashModeActivationCountsRequestSchema: z.ZodType<RecordCashModeActivationCountsRequest> =
  z.object({
    companyId: z.uuid(),
    operationId: z.uuid(),
    activationId: z.uuid(),
    counts: z.array(CashModeActivationCountDtoSchema).nonempty(),
    occurredAt: timestampSchema.optional(),
  });

export const RecordCashModeActivationCountsResponseSchema: z.ZodType<RecordCashModeActivationCountsResponse> =
  z.object({
    activation: CashModeActivationDtoSchema,
    state: CashScopeModeStateDtoSchema,
  });

export const ConfirmCashModeActivationRequestSchema: z.ZodType<ConfirmCashModeActivationRequest> =
  z.object({
    companyId: z.uuid(),
    operationId: z.uuid(),
    activationId: z.uuid(),
    occurredAt: timestampSchema.optional(),
  });

export const ConfirmCashModeActivationResponseSchema: z.ZodType<ConfirmCashModeActivationResponse> =
  z.object({
    activation: CashModeActivationDtoSchema,
    state: CashScopeModeStateDtoSchema,
  });

export const CancelCashModeActivationRequestSchema: z.ZodType<CancelCashModeActivationRequest> =
  z.object({
    companyId: z.uuid(),
    operationId: z.uuid(),
    activationId: z.uuid(),
    reason: z.string().trim().min(1).max(500),
    occurredAt: timestampSchema.optional(),
  });

export const CancelCashModeActivationResponseSchema: z.ZodType<CancelCashModeActivationResponse> =
  z.object({
    activation: CashModeActivationDtoSchema,
    state: CashScopeModeStateDtoSchema,
  });

export const ReconcileCashControlEntitlementRequestSchema: z.ZodType<ReconcileCashControlEntitlementRequest> =
  z
    .object({
      companyId: z.uuid(),
      operationId: z.uuid(),
      scopeType: CashModeScopeTypeSchema,
      scopeId: z.uuid(),
      occurredAt: timestampSchema.optional(),
    })
    .strict();

export const ReconcileCashControlEntitlementResponseSchema: z.ZodType<ReconcileCashControlEntitlementResponse> =
  z.object({
    state: CashScopeModeStateDtoSchema,
    effect: CashControlEntitlementTransitionEffectSchema,
    safeBoundaryReached: z.boolean(),
  });
